package com.trevorwiebe.timesheet.core.domain

import com.trevorwiebe.timesheet.core.domain.model.Rate
import com.trevorwiebe.timesheet.core.domain.model.TimeSheet
import com.trevorwiebe.timesheet.payPeriod.presentation.uiUtils.TimeSheetStatus
import dev.gitlive.firebase.FirebaseException
import dev.gitlive.firebase.FirebaseNetworkException
import dev.gitlive.firebase.auth.FirebaseAuthInvalidCredentialsException
import dev.gitlive.firebase.auth.FirebaseAuthInvalidUserException
import dev.gitlive.firebase.auth.FirebaseAuthUserCollisionException
import kotlin.time.Clock
import kotlin.time.ExperimentalTime
import kotlinx.datetime.DateTimeUnit
import kotlinx.datetime.LocalDate
import kotlinx.datetime.LocalDateTime
import kotlinx.datetime.Month
import kotlinx.datetime.TimeZone
import kotlinx.datetime.atTime
import kotlinx.datetime.minus
import kotlinx.datetime.number
import kotlinx.datetime.toInstant
import kotlinx.datetime.toLocalDateTime
import kotlin.math.roundToInt
import kotlin.time.Duration
import kotlin.time.Duration.Companion.hours
import kotlin.time.Instant

object Util {
    fun toFriendlyTime(dateTime: LocalDateTime?): String {

        val hour = dateTime?.hour ?: return ""
        val minute = dateTime.minute

        val formattedHour = when {
            hour == 0 -> 12
            hour > 12 -> hour - 12
            hour == 12 -> 12
            else -> hour
        }

        val meridiem = if (hour >= 12) "PM" else "AM"

        return "${formattedHour}:${minute.toString().padStart(2, '0')} $meridiem"
    }

    fun toFriendlyDate(dateTime: LocalDate?): String {
        val year = dateTime?.year
        val month = dateTime?.month?.number
        val day = dateTime?.day
        return "$month-$day-$year"
    }

    fun toFriendlyDate(dateTime: LocalDateTime?): String {
        val year = dateTime?.year
        val month = dateTime?.month?.number
        val day = dateTime?.day
        return "$month-$day-$year"
    }

    fun toFriendlyDateTime(dateTime: LocalDateTime?): String {
        if (dateTime == null) return ""
        return "${toFriendlyDate(dateTime)}  ${toFriendlyTime(dateTime)}"
    }

    fun toFriendlyDayOfWeek(dateTime: LocalDate?): String {
        val dayOfWeek = dateTime?.dayOfWeek
        return "$dayOfWeek"
    }

    /** Full month name and day-of-month, e.g. "July 1". */
    fun toMonthDay(date: LocalDate?): String {
        if (date == null) return ""
        val month = date.month.name.lowercase().replaceFirstChar { it.uppercase() }
        return "$month ${date.day}"
    }

    /** Abbreviated day-of-week and day-of-month, e.g. "Wed - 1". */
    fun toShortDayOfWeekDay(date: LocalDate?): String {
        if (date == null) return ""
        val dayOfWeek = date.dayOfWeek.name.lowercase().replaceFirstChar { it.uppercase() }.take(3)
        return "$dayOfWeek - ${date.day}"
    }

    /** Day-of-week, full month name and day, e.g. "Wednesday, July 1". */
    fun toDayOfWeekMonthDay(date: LocalDate?): String {
        if (date == null) return ""
        val dayOfWeek = date.dayOfWeek.name.lowercase().replaceFirstChar { it.uppercase() }
        return "$dayOfWeek, ${toMonthDay(date)}"
    }

    /** Numeric month-day, e.g. "7-26". */
    fun toNumericMonthDay(date: LocalDate?): String {
        if (date == null) return ""
        return "${date.month.number}-${date.day}"
    }

    /** Full month name and year, e.g. "October 2026". */
    fun toMonthYear(date: LocalDate?): String {
        if (date == null) return ""
        val month = date.month.name.lowercase().replaceFirstChar { it.uppercase() }
        return "$month ${date.year}"
    }

    /** Abbreviated month, day and year, e.g. "Oct 3, 2026". */
    fun toShortMonthDayYear(date: LocalDate?): String {
        if (date == null) return ""
        val month = date.month.name.lowercase().replaceFirstChar { it.uppercase() }.take(3)
        return "$month ${date.day}, ${date.year}"
    }

    /** Material3 date pickers store each day as midnight UTC, so the calendar date is read back in UTC. */
    @OptIn(ExperimentalTime::class)
    fun datePickerMillisToDate(millis: Long): LocalDate =
        Instant.fromEpochMilliseconds(millis).toLocalDateTime(TimeZone.UTC).date

    @OptIn(ExperimentalTime::class)
    fun localDateTime(atTopOfDay: Boolean = false): LocalDateTime {
        val dateTime = Clock.System.now().toLocalDateTime(TimeZone.currentSystemDefault())
        return if (atTopOfDay) dateTime.date.atTime(0, 0) else dateTime
    }

    /**
     * The current calendar quarter as an inclusive [start, end] range: the first day of the
     * quarter through today. Shared default date window for the mileage log and reporting screens.
     */
    @OptIn(ExperimentalTime::class)
    fun currentQuarterRange(): Pair<LocalDate, LocalDate> {
        val today = Clock.System.now().toLocalDateTime(TimeZone.currentSystemDefault()).date
        val quarterStartMonth = ((today.month.number - 1) / 3) * 3 + 1
        val quarterStart = LocalDate(today.year, Month(quarterStartMonth), 1)
        return quarterStart to today
    }

    /**
     * A rolling two-month window as an inclusive [start, end] range: two months before today
     * through today. Default date window for the mileage log.
     */
    @OptIn(ExperimentalTime::class)
    fun lastTwoMonthsRange(): Pair<LocalDate, LocalDate> {
        val today = Clock.System.now().toLocalDateTime(TimeZone.currentSystemDefault()).date
        val start = today.minus(2, DateTimeUnit.MONTH)
        return start to today
    }

    fun LocalDateTime.toNearestFifteenMinutes(): LocalDateTime {
        val totalMinutes = this.hour * 60 + this.minute
        val roundedTotalMinutes = (totalMinutes / 15.0).roundToInt() * 15

        // If rounding would go to next day (1440 minutes = 24:00), cap at 23:59:59.999
        if (roundedTotalMinutes >= 1440) {
            return LocalDateTime(year = year,
                month = month.number,
                day = day,
                hour = 23,
                minute = 59,
                second = 59,
                nanosecond = 0
            )
        }

        val newHour = roundedTotalMinutes / 60
        val newMinute = roundedTotalMinutes % 60

        return LocalDateTime(year = year,
            month = month.number,
            day = day,
            hour = newHour,
            minute = newMinute,
            second = 0,
            nanosecond = 0
        )
    }

    @OptIn(ExperimentalTime::class)
    fun LocalDateTime.plusHours(hours: Int): LocalDateTime {
        val timeZone = TimeZone.currentSystemDefault()
        return try {
            this.toInstant(timeZone)
                .plus(hours.hours)
                .toLocalDateTime(timeZone)
        } catch (e: Exception) {
            defaultDate()
        }
    }

    @OptIn(ExperimentalTime::class)
    fun convertStringToLocalDateTime(dateTimeString: String): LocalDateTime {
        if (dateTimeString.isEmpty() || dateTimeString == "null") return defaultDate()
        return try {
            Instant.parse(dateTimeString)
                .toLocalDateTime(TimeZone.currentSystemDefault())
        } catch (e: Exception) {
            defaultDate()
        }
    }

    @OptIn(ExperimentalTime::class)
    fun LocalDateTime.toIsoStringWithMillis(timeZone: TimeZone): String {
        val instantString = this.toInstant(timeZone).toString()
        return if (instantString.contains(".")) {
            instantString
        } else {
            instantString.dropLast(1) + ".000Z"
        }
    }

    fun convertStringToLocalDate(dateString: String): LocalDate {
        if (dateString.isEmpty() || dateString == "null") return defaultDate().date
        return try {
            LocalDate.parse(dateString)
        } catch (e: Exception) {
            defaultDate().date
        }
    }

    // Extension function to calculate duration between two Instants
    @OptIn(ExperimentalTime::class)
    fun Duration.Companion.between(start: LocalDateTime, end: LocalDateTime): Duration {
        return end.toInstant(TimeZone.currentSystemDefault()) - start.toInstant(TimeZone.currentSystemDefault())
    }

    fun getReadableErrorMessage(e: FirebaseException): String {
        return when {
            e is FirebaseAuthInvalidUserException -> "No account exists with this email. Please contact your manager."
            e is FirebaseAuthInvalidCredentialsException -> "Invalid username or password. Please try again."
            e is FirebaseAuthUserCollisionException -> "An account already exists with this email."
            e is FirebaseNetworkException -> "Network error. Please check your connection and try again."
            e.message?.contains("INVALID_LOGIN_CREDENTIALS") == true -> "Invalid email or password. Please check your credentials."
            e.message?.contains("TOO_MANY_ATTEMPTS_TRY_LATER") == true -> "Too many unsuccessful login attempts. Please try again later."
            e.message?.contains("EMAIL_NOT_FOUND") == true -> "No account exists with this email address."
            e.message?.contains("INVALID_PASSWORD") == true -> "The password is invalid for this account."
            e.message?.contains("USER_DISABLED") == true -> "This account has been disabled."
            else -> "Authentication failed: ${e.message}"
        }
    }

    fun getTimeSheetStatus(
        timeSheet: TimeSheet?,
        currentPeriod: Boolean,
    ): List<TimeSheetStatus> {

        if (timeSheet == null) return emptyList()

        val statusList = mutableListOf<TimeSheetStatus>()

        // Check if the time sheet is in the current pay period
        if (currentPeriod) {
            statusList.add(TimeSheetStatus.CURRENT_PERIOD)
        }
        if (timeSheet.confirmedByUser.not()) {
            // Check if the time sheet needs to be confirmed
            statusList.add(TimeSheetStatus.CONFIRM_HOURS_NOW)
        }
        if (timeSheet.submitted.not() && timeSheet.confirmedByUser) {
            statusList.add(TimeSheetStatus.CONFIRMED)
        }

        if (timeSheet.submitted && timeSheet.confirmedByUser) {
            statusList.add(TimeSheetStatus.PERIOD_CLOSED)
        }

        return statusList
    }

    fun defaultDate(): LocalDateTime {
        // date when android was released, because it's better than apple
        return LocalDateTime(2008, 9, 23, 0, 0, 0)
    }

    fun getRateIDFromName(name: String, rates: List<Rate>): String {
        if(rates.isEmpty()) return ""
        return rates.filter { it.description == name }.map { it.id }.firstOrNull() ?: ""
    }

    private val emailRegex = Regex("^[^@\\s]+@[^@\\s]+\\.[^@\\s]+$")

    fun isValidEmail(email: String): Boolean {
        return emailRegex.matches(email)
    }

    /**
     * Validates a US phone number: 10 digits with an optional leading 1 or +1,
     * where the area code starts with 2-9. Common formatting characters
     * (spaces, dashes, dots, parentheses) are allowed.
     */
    fun isValidUsPhone(phone: String): Boolean {
        val trimmed = phone.trim()
        if (trimmed.any { !it.isDigit() && it !in " ()-.+" }) return false
        if ('+' in trimmed && !trimmed.startsWith("+1")) return false

        val digits = trimmed.filter { it.isDigit() }
        val national = when {
            digits.length == 11 && digits.startsWith("1") -> digits.drop(1)
            digits.length == 10 -> digits
            else -> return false
        }
        return national[0] in '2'..'9'
    }

    /** Minimal RFC 3986 percent-encoding for a URL query value. */
    fun percentEncode(value: String): String = buildString {
        for (byte in value.encodeToByteArray()) {
            val char = byte.toInt().toChar()
            if (char in 'A'..'Z' || char in 'a'..'z' || char in '0'..'9' || char in "-._~") {
                append(char)
            } else {
                append('%')
                append(byte.toUByte().toString(16).uppercase().padStart(2, '0'))
            }
        }
    }
}